<template>
  <b-container class="square-terminal-location-list">
    <b-row v-if="testMode">
      <b-col>
        <div
          class="square-sandbox-mode-notice"
          data-cy="square-sandbox-mode-notice"
        >
          <font-awesome-icon
            icon="triangle-exclamation"
            class="notice-icon"
            size="lg"
          />
          <div class="notice-content">
            <strong>Sandbox mode active.</strong>
            Locations and devices added here will not appear in the list &mdash; sandbox mode uses a predefined hard-coded set of locations and devices to enable payment processing.
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="noLocations && !loading && !formActive">
      <b-col>
        <div class="no-locations">
          <div class="icon-container">
            <font-awesome-icon
              icon="location-crosshairs"
              size="3x"
              class="icon"
              fixed-width
            />
          </div>
          <div class="message">
            There are no locations configured. Please add one.
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Location List - Hidden when form or confirmation is active -->
    <b-row v-if="!loading && !noLocations && !formActive && !confirmationActive">
      <b-col>
        <div class="location-list-wrapper">
          <div
            v-for="(location, idx) in locations"
            :key="idx"
            class="location"
          >
            <square-location-details
              :location="location"
              :remove="true"
              @removed="locationRemoved"
              @error="locationRemoveError"
              @deactivated="showDeactivateConfirmation"
              @activated="showActivateConfirmation"
              @edit-requested="editLocation"
            />
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="!loading">
      <b-col>
        <b-row v-if="message">
          <b-col>
            <div
              class="message"
              :class="message.type"
              @click="dismissMessages"
            >
              <div class="inner">
                <font-awesome-icon
                  v-if="!errorMessage"
                  icon="check-circle"
                  size="2x"
                  class="icon"
                  fixed-width
                  color="green"
                />
                <font-awesome-icon
                  v-if="errorMessage"
                  icon="triangle-exclamation"
                  size="2x"
                  class="icon"
                  fixed-width
                  color="red"
                />
                <div>{{ message.text }}</div>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Add Location Button - Hidden when form or confirmation is active -->
        <div
          v-if="!formActive && !confirmationActive && !noLocations"
          class="add-terminal-location"
        >
          <b-button
            variant="primary"
            @click="showAddLocationForm"
          >
            Add terminal location
          </b-button>
        </div>

        <!-- Show Add button when no locations exist and no form/confirmation is active -->
        <div
          v-if="!formActive && !confirmationActive && noLocations"
          class="add-terminal-location"
        >
          <b-button
            variant="primary"
            @click="showAddLocationForm"
          >
            Add first terminal location
          </b-button>
        </div>

        <!-- Location Form Section - Replaces the location list when active -->
        <div
          v-if="formActive"
          class="location-form-section"
        >
          <div class="form-header">
            <h3>{{ formTitle }}</h3>
          </div>

          <square-location-add
            :edit-mode="isEditMode"
            :existing-location="editingLocation"
            @success="formSuccess"
            @error="formError"
            @cancel="cancelForm"
          />
        </div>

        <!-- Confirmation Section - Replaces the location list when active -->
        <div
          v-if="confirmationActive"
          class="confirmation-form-section"
        >
          <deactivate-location-confirmation
            v-if="confirmationType === 'deactivate'"
            :location="confirmationLocation"
            @deactivated="handleDeactivated"
            @cancel="cancelConfirmation"
          />
          <activate-location-confirmation
            v-if="confirmationType === 'activate'"
            :location="confirmationLocation"
            @activated="handleActivated"
            @cancel="cancelConfirmation"
          />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import squareLocationDetails from './SquareLocationDetails.vue'
import squareLocationAdd from './SquareLocationAdd.vue'
import DeactivateLocationConfirmation from './DeactivateLocationConfirmation.vue'
import ActivateLocationConfirmation from './ActivateLocationConfirmation.vue'

export default {
  name: 'square-location-list',
  components: {
    squareLocationDetails,
    squareLocationAdd,
    DeactivateLocationConfirmation,
    ActivateLocationConfirmation
  },
  data: function() {
    return {
      loading: false,
      message: null,
      formActive: false,
      isEditMode: false,
      editingLocation: null,
      formTitle: 'Add New Terminal Location',
      confirmationActive: false,
      confirmationType: null,
      confirmationLocation: null
    }
  },
  computed: {
    locations: function() {
      return this.$store.state.square.locations || []
    },
    noLocations: function() {
      return this.locations.length === 0
    },
    errorMessage: function() {
      return (this.message && this.message.type === 'error')
    },
    testMode: function() {
      return this.$store.getters['square/testMode']
    }
  },
  watch: {
    loading: function(newVal) {
      if (newVal) {
        this.$emit('loading', true)
      }
      else {
        this.$emit('loading', false)
      }
    }
  },
  mounted: function() {
    this.loadLocations()
  },
  methods: {
    loadLocations: function() {
      this.loading = true
      this.$store.dispatch('square/loadLocations', { force: true })
        .then(_ => {
          setTimeout(() => {
            this.loading = false
          }, 500)
        })
        .catch(err => {
          this.loading = false
          this.displayMessage(`Error loading terminal locations: ${err.message}`, 'error')
          console.error(err)
        })
    },
    displayMessage: function(message, type, timeout) {
      this.message = {
        type: (type === undefined) ? 'success' : 'error',
        text: message
      }
      setTimeout(() => {
        this.message = null
      }, timeout === undefined ? 15000 : timeout)
    },
    dismissMessages: function(type) {
      this.message = null
    },
    newLocationAdded: function(address) {
      this.hideAddLocationModal()
      this.loading = true
      this.$store.dispatch('square/loadLocations', { force: true })
        .then(_ => {
          setTimeout(() => {
            this.loading = false
          }, 500)
        })
        .catch(err => {
          this.loading = false
          this.$emit('loading', false)
          this.displayMessage(`Error loading terminal locations: ${err.message}`, 'error')
          console.error(err)
        })
      this.displayMessage(`New location ${address.name || address.display_name} was successfully added.`)
    },
    newLocationError: function({ address, error }) {
      this.hideAddLocationModal()
      this.displayMessage(`Error adding new location ${address.name || address.display_name} -- ${error.message}`, 'error')
    },
    locationRemoved: function(location) {
      this.displayMessage(`Location ${location.id} - ${location.display_name} was removed successfully.`)
    },
    locationRemoveError: function({ location, error }) {
      this.displayMessage(
        `Location ${location.id} - ${location.display_name} could not be removed.<br>Reason: ${error.message}`,
        'error'
      )
    },
    locationDeactivated: function(location) {
      this.confirmationActive = true
      this.confirmationType = 'deactivate'
      this.confirmationLocation = location
    },
    locationActivated: function(location) {
      this.confirmationActive = true
      this.confirmationType = 'activate'
      this.confirmationLocation = location
    },
    confirmDeactivate(location) {
      this.loading = true
      this.$store.dispatch('square/deactivateLocation', location.id)
        .then(_ => {
          this.loading = false
          this.displayMessage(`Location ${location.name} was deactivated successfully.`)
          this.loadLocations()
        })
        .catch(err => {
          this.loading = false
          this.displayMessage(`Error deactivating location ${location.name}: ${err.message}`, 'error')
          console.error(err)
        })
      this.confirmationActive = false
    },
    confirmActivate(location) {
      this.loading = true
      this.$store.dispatch('square/activateLocation', location.id)
        .then(_ => {
          this.loading = false
          this.displayMessage(`Location ${location.name} was activated successfully.`)
          this.loadLocations()
        })
        .catch(err => {
          this.loading = false
          this.displayMessage(`Error activating location ${location.name}: ${err.message}`, 'error')
          console.error(err)
        })
      this.confirmationActive = false
    },
    locationEdited: function(location) {
      this.loading = true
      this.$store.dispatch('square/loadLocations', { force: true })
        .then(_ => {
          setTimeout(() => {
            this.loading = false
          }, 500)
        })
        .catch(err => {
          this.loading = false
          this.$emit('loading', false)
          this.displayMessage(`Error loading terminal locations: ${err.message}`, 'error')
          console.error(err)
        })
      this.displayMessage(`Location ${location.name || location.display_name} was updated successfully.`)
    },
    showAddLocationForm() {
      this.formActive = true
      this.isEditMode = false
      this.editingLocation = null
      this.formTitle = 'Add New Terminal Location'
    },
    cancelForm() {
      this.formActive = false
      this.editingLocation = null
    },
    editLocation(location) {
      this.formActive = true
      this.isEditMode = true
      this.editingLocation = location
      this.formTitle = `Edit Terminal Location - ${location.name}`
    },
    formSuccess(location) {
      this.formActive = false
      this.displayMessage(`Location ${location.name || location.display_name} was successfully ${this.isEditMode ? 'updated' : 'added'}.`)
      this.loadLocations()
    },
    formError({ location, error }) {
      this.displayMessage(`Error ${this.isEditMode ? 'updating' : 'adding'} location ${location.name || location.display_name} -- ${error.message}`, 'error')
    },
    showDeactivateConfirmation(location) {
      this.message = null // Clear any existing messages
      this.confirmationActive = true
      this.confirmationType = 'deactivate'
      this.confirmationLocation = location
    },
    showActivateConfirmation(location) {
      this.message = null // Clear any existing messages
      this.confirmationActive = true
      this.confirmationType = 'activate'
      this.confirmationLocation = location
    },
    handleDeactivated(location) {
      this.confirmationActive = false
      this.displayMessage(`Location ${location.name} was deactivated successfully.`)
      this.loadLocations()
    },
    handleActivated(location) {
      this.confirmationActive = false
      this.displayMessage(`Location ${location.name} was activated successfully.`)
      this.loadLocations()
    },
    cancelConfirmation() {
      this.confirmationActive = false
      this.confirmationType = null
      this.confirmationLocation = null
    }
  }
}
</script>

<style lang="scss" scoped>
  .square-terminal-location-list {

    .square-sandbox-mode-notice {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      padding: 0.75rem 1rem;
      margin-bottom: 1rem;
      background-color: #fff3cd;
      border: 1px solid #ffeeba;
      border-left: 4px solid #ff9800;
      border-radius: 4px;
      color: #663c00;
      line-height: 1.4;

      .notice-icon {
        color: #ff9800;
        margin-right: 0.6rem;
        flex-shrink: 0;
        margin-top: 0.15rem;
      }

      .notice-content {
        flex: 1;

        strong {
          margin-right: 0.25rem;
        }
      }
    }

    .no-locations {
      display: flex;
      flex-direction: row;
      justify-content: center;
      padding: 2.2rem;
      align-items: center;
    }

    .location {
      border: 1px solid black;
      padding: 20px 10px;
      display: flex;
      flex-direction: column;
      min-height: 180px; /* Set minimum height instead of height: 100% */
    }

    .location-list-wrapper {
      padding: 0;
      list-style: none;
      display: grid;
      grid-auto-columns: 1fr;
      grid-template-columns: repeat(2, 1fr);
      gap: .5rem;
      /* Removed grid-auto-rows: 1fr to allow natural height */
    }

    .add-terminal-location {
      margin-top: 2.2rem;
    }

    .location-form-section,
    .confirmation-form-section {
      margin-top: 2.2rem;
      padding: 1.2rem;
      border: 1px solid #dee2e6;
      border-radius: 0.5rem;
      background-color: #f9f9f9;

      .form-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.2rem;

        h3 {
          margin: 0;
          font-size: 1.5rem;
          color: #333;
        }
      }

      .form-actions {
        display: flex;
        gap: 0.5rem;
      }
    }
  }

  .message {
    padding: 1.2rem 0.8rem;
    &.error {
      .inner {
        background-color: lightpink;
        color: darkred;
      }
    }

    &.success {
      .inner {
        background-color: lightgreen;
        color: darkgreen;
      }
    }

    .inner {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 1.2rem 0.5rem;
      div {
        margin-left: 2.2rem;
      }
    }
  }

  .modal-header-custom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid #dee2e6;
  }

  .close-button {
    padding: 0.2rem 0.5rem;
  }

  .square-add-location-modal {
    /* Custom styles for the modal */
    .modal-content {
      border-radius: 0.5rem;
      overflow: hidden;
    }
  }

  .square-add-location-modal-body {
    padding: 2rem;
  }

  .square-add-location-modal-header {
    background-color: #f7f7f9;
    border-bottom: 1px solid #dee2e6;
  }
</style>
